import type { Point } from 'jsxgraph';
import {
  bindGrabHandle,
  bindCanvasSelections,
  boundingBoxToTuple,
  type CanvasSelectionConfig,
  createElementFactory,
  JsxgraphBaseWidget,
  type JsxgraphStatic,
  type JsxgraphThemeService,
  type ParamHandle,
  WIDGET_CONTROL_TYPE,
  type WidgetCanvasSelectionControl,
  type WidgetControl,
  type ActionDefinition,
  type WidgetState,
} from '@examdojo/core/widget';
import type {
  GeometryElement,
  GeometryElementDef,
  GeometryPadWidgetConfig,
  GeometryDisplayConfig,
} from './geometrypad.model';
import { GEOMETRY_DISPLAY_DEFAULTS, GEOMETRYPAD_COORDINATE_SYSTEM_DEFAULTS } from './geometrypad.model';
import { PortRegistry, registerSolvedPortsOnRegistry, resolveValue } from './port';
import { canonicalPlace } from './placement';
import { computeAutoBoundingBox } from './placement';
import { resolveVertex, applyTransform } from './placement';
import type { PlacedShape } from './placement';
import type { SolvedShape } from './analytics';
import type { CreatedGeometryElement, GeometryPadElementContext } from './elements';
import { createAllElements, resolveElementOrder } from './elements';
import { createLengthAnnotation } from './elements/annotations/length-annotation';
import { createArrowAnnotation } from './elements/annotations/arrow-annotation';
import { DEFAULT_GEOMETRY_SHAPE_THEME } from './elements/coloring/geometry-shape-theme';
const ANNOTATION_TYPES = new Set(['LengthAnnotation', 'ArrowAnnotation']);

/**
 * Extract all params from a geometry element definition.
 * Returns every entry in the element's params object — numeric (side lengths,
 * angles), string refs (parent element bindings like `of`), PointTargets, etc.
 *
 * The `ParameterizedElement` interface is typed `Record<string, number>` for
 * the `UPDATE_PARAM` animation path, but at runtime any param type can be
 * read/written through the same handle. Non-numeric params won't animate
 * meaningfully, but they are programmatically accessible for external mutation.
 */
function extractElementParams(elementDef: GeometryElementDef): Record<string, unknown> {
  if (!('params' in elementDef) || !elementDef.params) {
    return {};
  }
  return { ...(elementDef.params as Record<string, unknown>) };
}

/**
 * GeometryPad widget - declarative geometric shape visualization.
 *
 * Supports triangles, rectangles, squares, circles, polygons, points, and lines
 * with expression-based positional params, interactivity, and configurable styling.
 */
export class GeometryPadWidget extends JsxgraphBaseWidget<GeometryPadWidgetConfig> {
  constructor(container: HTMLElement, jsxInstance: JsxgraphStatic, themeService: JsxgraphThemeService) {
    super(container, jsxInstance, themeService);
    this.id = `geometrypad-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  }

  readonly id: string;
  readonly type = 'geometrypad';

  private config?: GeometryPadWidgetConfig;
  private readonly createdElements: Map<string, CreatedGeometryElement> = new Map();
  private portRegistry?: PortRegistry;
  private readonly variableValues = new Map<string, number>();

  protected getCurrentConfig(): GeometryPadWidgetConfig | undefined {
    return this.config;
  }

  getState(): WidgetState {
    return {
      type: this.type,
      id: this.id,
      params: this.config?.params,
    };
  }

  resolveValue(path: string): number | string | undefined {
    if (!this.portRegistry) {
      return undefined;
    }
    try {
      if (this.portRegistry.hasPort(path)) {
        const value = this.portRegistry.resolve(path);
        if (typeof value === 'number') {
          return value;
        }
        if (typeof value === 'string') {
          return value;
        }
      }
    } catch {
      // Port resolution can throw on invalid paths
    }
    return undefined;
  }

  override resolveParam(path: string): ParamHandle | null {
    // Bare variable paths (e.g., 'k', 'theta') — not dot-delimited,
    // so the base class won't match them in parameterizedElements.
    if (this.variableValues.has(path)) {
      return {
        get: () => this.variableValues.get(path)!,
        set: (value: number) => {
          this.variableValues.set(path, value);
          this.updateElementPositions();
        },
      };
    }

    // Element.param paths (e.g., 't1.a') are handled by the base class
    // via the parameterizedElements map populated in initElements().
    return super.resolveParam(path);
  }

  init(config: GeometryPadWidgetConfig): void {
    this.config = config;
    const { display } = config;

    // Initialize variables from config
    if (config.params.variables) {
      for (const [name, value] of Object.entries(config.params.variables)) {
        this.variableValues.set(name, value);
      }
    }

    // Compute bounding box before board init
    const autoBbox = computeAutoBoundingBox(config.params);
    const bbox = display?.coordinateSystem?.boundingbox ?? autoBbox;
    let boundingbox = boundingBoxToTuple(bbox);

    // Apply geometrypad-specific coordinate system defaults (axes hidden, grid hidden, fixedAxis: 'x').
    const coordinateSystem = display?.coordinateSystem;
    const fixedAxis = coordinateSystem?.fixedAxis ?? GEOMETRYPAD_COORDINATE_SYSTEM_DEFAULTS.fixedAxis;

    // Adjust bounding box for equal units when fixedAxis is set
    if (fixedAxis) {
      boundingbox = this.adjustBboxForFixedAxis(boundingbox, fixedAxis);
    }

    // Initialize board (one-time setup)
    this.initBoard({
      boundingbox,
      axis: false,
    });

    // Set up resize observer to maintain equal units when fixedAxis is configured
    if (fixedAxis) {
      this.setupFixedAxisObserver();
    }

    // Always create the coordinate system, applying geometrypad defaults
    // (axes hidden, grid hidden — unlike graphpad which shows both).
    this.createCoordinateSystem({
      ...coordinateSystem,
      axes: coordinateSystem?.axes ?? GEOMETRYPAD_COORDINATE_SYSTEM_DEFAULTS.axes,
      grid: coordinateSystem?.grid ?? GEOMETRYPAD_COORDINATE_SYSTEM_DEFAULTS.grid,
    });

    // Create elements (re-runnable for variable changes)
    this.initElements();

    // Initialize canvas selection controls (on-canvas radio buttons)
    this.initCanvasSelectionsWithPorts(config.behavior?.controls);
  }

  override destroy(): void {
    // Destroy grab handles added post-creation (e.g. via makePointInteractive)
    for (const element of this.createdElements.values()) {
      if (element.grabHandles) {
        for (const gh of element.grabHandles) {
          gh.destroy();
        }
      }
    }
    this.destroyCreatedElements(this.createdElements);
    super.destroy();
    this.portRegistry = undefined;
  }

  /**
   * Initialize canvas selections with expression-based location resolution.
   * Each `location: { x, y }` is resolved: numbers pass through, strings are
   * evaluated as port expressions on every board update.
   */
  private initCanvasSelectionsWithPorts(controls: WidgetControl[] | undefined): void {
    if (!controls || !this.board) {
      return;
    }

    const canvasSelectionControls = controls.filter(
      (c): c is WidgetCanvasSelectionControl => c.type === WIDGET_CONTROL_TYPE.CanvasSelection,
    );

    if (canvasSelectionControls.length === 0) {
      return;
    }

    const configs: CanvasSelectionConfig[] = canvasSelectionControls.map((c) => {
      const resolveAxis = (val: number | string): number | (() => number) => {
        if (typeof val === 'number') {
          return val;
        }
        return () => resolveValue(val, this.portRegistry!);
      };

      return {
        id: c.id,
        x: resolveAxis(c.location.x),
        y: resolveAxis(c.location.y),
        label: c.label,
        trigger: c.trigger,
        triggerOff: c.triggerOff,
        group: c.group,
        selected: c.selected,
      };
    });

    this.canvasSelections = bindCanvasSelections({
      board: this.board,
      themeService: this.themeService,
      configs,
      onSelect: async (config, selected) => {
        if (selected) {
          await this.executeAction(config.trigger as ActionDefinition);
        } else if (config.triggerOff) {
          await this.executeAction(config.triggerOff as ActionDefinition);
        }
      },
    });

    this.canvasSelections.getAllJsxObjects().forEach((obj, idx) => {
      this.objects.set(`canvas-selection-${idx}`, obj);
    });
  }

  /**
   * Create all geometry elements on the board.
   * Called once during init().
   */
  private initElements(): void {
    if (!this.config) {
      return;
    }
    const { params, display, behavior } = this.config;

    // 1. Create port registry and register variables
    const portRegistry = new PortRegistry();
    this.portRegistry = portRegistry;
    this.registerVariablesOnRegistry(portRegistry);

    // 2. Convert Record → array with id injected, then resolve order
    const elements: GeometryElement[] = Object.entries(params.elements).map(([id, def]) => ({ ...def, id }));
    const sortedElements = resolveElementOrder(elements);

    // 3. Incrementally solve, place, and register world-space ports
    const { solvedShapes, placedShapes } = this.solveAndPlaceElements(sortedElements, portRegistry);

    // 6. Create element factory
    const elementFactory = createElementFactory(this.board, this.container, this.themeService, {
      defaultPrecision: display?.math?.defaultPrecision,
    });

    // 7. Build glide target map from Point.glideOn params
    const glideTargets = new Map<string, string>();
    for (const element of sortedElements) {
      if (element.type === 'Point') {
        const pointParams = element.params as { glideOn?: string };
        if (pointParams.glideOn) {
          glideTargets.set(element.id, pointParams.glideOn);
        }
      }
    }

    // 8. Create context
    const geometryConfig: GeometryDisplayConfig = {
      shape: {
        coloring: display?.geometry?.shape?.coloring ?? GEOMETRY_DISPLAY_DEFAULTS.shape.coloring,
        fill: display?.geometry?.shape?.fill ?? GEOMETRY_DISPLAY_DEFAULTS.shape.fill,
      },
      angle: {
        rightAngleDisplay:
          display?.geometry?.angle?.rightAngleDisplay ?? GEOMETRY_DISPLAY_DEFAULTS.angle.rightAngleDisplay,
        coloring: display?.geometry?.angle?.coloring ?? GEOMETRY_DISPLAY_DEFAULTS.angle.coloring,
        fill: display?.geometry?.angle?.fill ?? GEOMETRY_DISPLAY_DEFAULTS.angle.fill,
      },
      label: {
        names: display?.geometry?.label?.names,
        values: display?.geometry?.label?.values,
        coloring: display?.geometry?.label?.coloring ?? GEOMETRY_DISPLAY_DEFAULTS.label.coloring,
        angleUnit: display?.geometry?.label?.angleUnit ?? GEOMETRY_DISPLAY_DEFAULTS.label.angleUnit,
        lengthUnit: display?.geometry?.label?.lengthUnit,
      },
    };

    const context: GeometryPadElementContext = {
      elementFactory,
      portRegistry,
      geometryConfig,
      displayConfig: display ?? {},
      glideTargets,
      shapeTheme: DEFAULT_GEOMETRY_SHAPE_THEME,
      defaultPrecision: display?.math?.defaultPrecision,
    };

    // 9. Create all JSXGraph elements
    const created = createAllElements(sortedElements, placedShapes, solvedShapes, context);
    for (const [id, el] of created) {
      this.createdElements.set(id, el);
      el.jsxObjects.forEach((obj, idx) => {
        this.objects.set(`${id}-${idx}`, obj);
      });
      // Register named objects for PATCH_DISPLAY targeting (e.g., 't1.edge.BC')
      if (el.namedObjects) {
        for (const [name, obj] of Object.entries(el.namedObjects)) {
          this.objects.set(`${id}.${name}`, obj);
        }
      }

      // Register as ParameterizedElement for UPDATE_PARAM support.
      // Exposes ALL params (numeric, string refs, objects) so elements can
      // be mutated programmatically from outside. Numeric params animate via
      // UPDATE_PARAM; non-numeric params (bindings like `of`, `from`) are
      // read/writable through the same getParams/setParams interface.
      const elementDef = params.elements[id];
      if (elementDef && Object.keys(extractElementParams(elementDef)).length > 0) {
        this.registerParameterizedElement(id, {
          getParams: () => extractElementParams(this.config!.params.elements[id]) as Record<string, number>,
          setParams: (newParams: Record<string, number>) => {
            const def = this.config!.params.elements[id];
            if (def && 'params' in def && def.params) {
              for (const [key, value] of Object.entries(newParams)) {
                (def.params as Record<string, unknown>)[key] = value;
              }
            }
            this.updateElementPositions();
          },
        });
      }
    }

    // 10. Create annotations
    for (const element of sortedElements) {
      if (element.type !== 'LengthAnnotation' && element.type !== 'ArrowAnnotation') {
        continue;
      }
      try {
        const result =
          element.type === 'LengthAnnotation'
            ? createLengthAnnotation(element, context)
            : createArrowAnnotation(element, context);

        if (Object.keys(result.ports).length > 0) {
          portRegistry.register(element.id, result.ports);
        }
        this.createdElements.set(element.id, result);
      } catch (error) {
        console.error(`Failed to create '${element.type}' element '${element.id}':`, error);
      }
    }

    // 11. Apply interactivity
    if (behavior?.interactive) {
      for (const portRef of behavior.interactive) {
        this.makeElementInteractive(portRef);
      }
    }
  }

  /**
   * Re-solve geometry with current variable values and move existing JSXGraph
   * points in-place via moveTo(). All dependent elements (borders, midpoints,
   * labels, angle arcs) update automatically through JSXGraph's dependency graph.
   * This avoids the destroy/recreate cycle that causes flickering.
   */
  private updateElementPositions(): void {
    if (!this.board || !this.config) {
      return;
    }
    const { params } = this.config;

    // 1. Re-solve shapes incrementally with updated variable values
    const portRegistry = new PortRegistry();
    this.registerVariablesOnRegistry(portRegistry);

    const elements: GeometryElement[] = Object.entries(params.elements).map(([id, def]) => ({ ...def, id }));
    const sortedElements = resolveElementOrder(elements);
    const { placedShapes } = this.solveAndPlaceElements(sortedElements, portRegistry);

    // 2. Move existing vertex points to new positions
    const board = this.board as unknown as {
      suspendUpdate: () => void;
      unsuspendUpdate: () => void;
    };
    board.suspendUpdate();
    for (const [elementId, created] of this.createdElements) {
      const placed = placedShapes.get(elementId);
      if (!placed || !created.vertexPoints) {
        continue;
      }

      for (const [vertexName, point] of Object.entries(created.vertexPoints)) {
        const vertexSource = placed.vertices[vertexName];
        if (vertexSource) {
          const newPos = resolveVertex(vertexSource);
          (point as unknown as { moveTo: (c: [number, number]) => void }).moveTo(newPos);
        }
      }
    }
    board.unsuspendUpdate();
  }

  /**
   * Incrementally solve, place, and register world-space ports for all elements.
   *
   * Each element is canonically placed (solved in local space), then positioned
   * via positional params or auto-layout, and its ports are re-registered with
   * world-space coordinates so downstream elements can reference correct positions.
   */
  private solveAndPlaceElements(
    sortedElements: GeometryElement[],
    portRegistry: PortRegistry,
  ): { solvedShapes: Map<string, SolvedShape>; placedShapes: Map<string, PlacedShape> } {
    const solvedShapes = new Map<string, SolvedShape>();
    const placedShapes = new Map<string, PlacedShape>();
    let autoLayoutMaxX = 0;
    const AUTO_LAYOUT_GAP = 3;

    for (const element of sortedElements) {
      if (ANNOTATION_TYPES.has(element.type)) {
        continue;
      }

      try {
        const result = canonicalPlace(element, portRegistry);
        if (result.solved) {
          solvedShapes.set(element.id, result.solved);
        }

        // Register ports with canonical values (enables expression resolution)
        registerSolvedPortsOnRegistry(element, result.solved, result.vertices, portRegistry);

        // Check if canonical-placement already resolved world-space positions
        let placed: PlacedShape | undefined;
        if (result.isPositioned) {
          placed = {
            elementId: element.id,
            type: element.type,
            vertices: result.vertices,
            transform: result.transform ?? { rotation: 0, translation: [0, 0] },
            isReactive: false,
          };
        }

        // Fall back to auto-layout
        if (!placed) {
          const canonical = result.vertices;
          if (Object.keys(canonical).length > 0) {
            let localMinX = Infinity;
            for (const coord of Object.values(canonical)) {
              localMinX = Math.min(localMinX, coord[0]);
            }
            const offsetX = autoLayoutMaxX + AUTO_LAYOUT_GAP - localMinX;
            const worldVertices: Record<string, [number, number]> = {};
            for (const [name, coord] of Object.entries(canonical)) {
              worldVertices[name] = [coord[0] + offsetX, coord[1]];
            }
            placed = {
              elementId: element.id,
              type: element.type,
              vertices: worldVertices,
              transform: { rotation: 0, translation: [offsetX, 0] },
              isReactive: false,
            };
            for (const coord of Object.values(worldVertices)) {
              autoLayoutMaxX = Math.max(autoLayoutMaxX, coord[0]);
            }
          }
        } else {
          for (const v of Object.values(placed.vertices)) {
            const coord = typeof v === 'function' ? v() : v;
            autoLayoutMaxX = Math.max(autoLayoutMaxX, coord[0]);
          }
        }

        if (placed) {
          placedShapes.set(element.id, placed);

          // Re-register ports with world-space vertices
          const worldVertices: Record<string, [number, number]> = {};
          for (const [name, coord] of Object.entries(result.vertices)) {
            worldVertices[name] = applyTransform(coord, placed.transform);
          }
          registerSolvedPortsOnRegistry(element, result.solved, worldVertices, portRegistry);
        }
      } catch (error) {
        console.error(`Failed to solve element '${element.id}':`, error);
      }
    }

    return { solvedShapes, placedShapes };
  }

  /**
   * Register all variables on a port registry so expressions can resolve them.
   */
  private registerVariablesOnRegistry(registry: PortRegistry): void {
    for (const name of this.variableValues.keys()) {
      registry.registerVariable(name, () => this.variableValues.get(name)!);
    }
  }

  /**
   * Make an element interactive by parsing the port ref.
   *
   * Supported refs:
   * - "elementId.point.vertexName" → vertex draggable (explore mode)
   * - "elementId" → whole shape translatable (translate mode)
   *
   * Edge refs ("elementId.edge.edgeName") are deprecated and ignored.
   * Point and translate are mutually exclusive per element.
   */
  private makeElementInteractive(portRef: string): void {
    const parts = portRef.split('.');
    const elementId = parts[0];
    const elementKind = parts.length > 1 ? parts[1] : undefined;

    const created = this.createdElements.get(elementId);
    if (!created) {
      return;
    }

    if (elementKind === 'point') {
      this.makePointInteractive(created, parts);
    } else if (elementKind === 'edge') {
      console.warn(`[GeometryPad] Edge interactivity is deprecated, ignoring "${portRef}". Use point refs instead.`);
    } else if (!elementKind) {
      this.makeShapeTranslatable(created);
    }
  }

  private makePointInteractive(created: CreatedGeometryElement, parts: string[]): void {
    const vertexName = parts.length > 2 ? parts[2] : undefined;

    for (const obj of created.jsxObjects) {
      const typed = obj as unknown as {
        elType?: string;
        name?: string;
        setAttribute?: (attrs: Record<string, unknown>) => void;
        visProp?: { fillcolor?: string };
      };
      if ((typed.elType === 'point' || typed.elType === 'glider') && typed.setAttribute) {
        if (!vertexName || typed.name === vertexName) {
          typed.setAttribute({ fixed: false, visible: true, size: 3 });

          // Add grab handle for newly interactive points
          const fillColor = typed.visProp?.fillcolor ?? this.themeService.resolveColorToken('sky');
          const grabHandle = bindGrabHandle({
            ctx: { board: this.board, container: this.container },
            point: obj as unknown as Point,
            color: fillColor,
          });
          created.jsxObjects.push(grabHandle.element);
          if (!created.grabHandles) {
            created.grabHandles = [];
          }
          created.grabHandles.push(grabHandle);
        }
      }
    }
  }

  /**
   * Make a shape translatable as a rigid body.
   *
   * Sets hasInnerPoints on the polygon so clicks inside start a drag,
   * but keeps ALL vertices fixed so JSXGraph doesn't try to move them.
   * A custom drag handler translates only the control points via moveTo().
   * Computed/dependent points follow via their defining functions.
   * Glider points project onto their (now translated) parent elements.
   */
  private makeShapeTranslatable(created: CreatedGeometryElement): void {
    const controlPoints = created.controlPoints;
    if (!controlPoints?.length) {
      return;
    }

    const polygon = created.jsxObjects.find((obj) => {
      const typed = obj as unknown as { elType?: string };
      return typed.elType === 'polygon';
    });
    if (!polygon) {
      return;
    }

    const board = (polygon as unknown as { board: import('jsxgraph').Board }).board;

    const typedPolygon = polygon as unknown as {
      setAttribute: (attrs: Record<string, unknown>) => void;
      on: (event: string, handler: (e: Event) => void) => void;
    };

    // Enable polygon hit detection, but keep all vertices fixed
    // so JSXGraph's default polygon drag doesn't move any vertices.
    typedPolygon.setAttribute({ hasInnerPoints: true, fixed: false, highlight: true });

    const typedPoints = controlPoints as unknown as Array<{
      X: () => number;
      Y: () => number;
      moveTo: (coords: [number, number]) => void;
    }>;

    // Custom drag: track mouse delta, translate only control points
    let lastCoords: [number, number] | null = null;

    typedPolygon.on('down', (e: Event) => {
      const coords = (board as unknown as { getUsrCoordsOfMouse: (e: Event) => number[] }).getUsrCoordsOfMouse(e);
      lastCoords = [coords[0], coords[1]];
    });

    typedPolygon.on('drag', (e: Event) => {
      if (!lastCoords) {
        return;
      }
      const coords = (board as unknown as { getUsrCoordsOfMouse: (e: Event) => number[] }).getUsrCoordsOfMouse(e);
      const dx = coords[0] - lastCoords[0];
      const dy = coords[1] - lastCoords[1];
      lastCoords = [coords[0], coords[1]];

      (board as unknown as { suspendUpdate: () => void }).suspendUpdate();
      for (const pt of typedPoints) {
        pt.moveTo([pt.X() + dx, pt.Y() + dy]);
      }
      (board as unknown as { unsuspendUpdate: () => void }).unsuspendUpdate();
    });

    typedPolygon.on('up', () => {
      lastCoords = null;
    });
  }
}
